// src/components/aiTalentDrive/DriveProof.tsx
// The Mumbai Tech Week track record on /ai-talent-drive: two headline numbers
// in a column beside click-to-play reels, in one grid.
//
// The reels are files in this repo (src/assets/reels), not an API: webpack
// gives them hashed names, so the deploy serves them with long-lived caching.
// A clip only downloads once it is clicked. To change a reel, replace its
// .mp4 (720x1280, ~1 Mbps, H.264/AAC, "faststart") and .jpg poster there and
// update the entry below.
import { useEffect, useRef, useState } from "react";
import { PlayIcon } from "@phosphor-icons/react";
import { CountUp } from "./driveMotion";
import reel1 from "../../assets/reels/mtw-1.mp4";
import reel2 from "../../assets/reels/mtw-2.mp4";
import reel3 from "../../assets/reels/mtw-3.mp4";
import poster1 from "../../assets/reels/mtw-1.jpg";
import poster2 from "../../assets/reels/mtw-2.jpg";
import poster3 from "../../assets/reels/mtw-3.jpg";

const STATS = [
  ["26,000+", "Candidates screened"],
  ["25", "Hiring companies"],
];

type Reel = { id: string; title: string; seconds: number; src: string; poster: string };

// seconds is the clip's length, shown on the tile
const REELS: Reel[] = [
  { id: "mtw-1", title: "MTW 2026 · Reel 1", seconds: 82, src: reel1, poster: poster1 },
  { id: "mtw-2", title: "MTW 2026 · Reel 2", seconds: 38, src: reel2, poster: poster2 },
  { id: "mtw-3", title: "MTW 2026 · Reel 3", seconds: 157, src: reel3, poster: poster3 },
];

const clock = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, "0")}`;

function ReelTile({
  reel,
  playing,
  onPlay,
  onStop,
}: {
  reel: Reel;
  playing: boolean;
  onPlay: () => void;
  onStop: () => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  // the button is replaced by the video on click: keep keyboard focus on the tile
  useEffect(() => {
    if (playing) video.current?.focus();
  }, [playing]);

  return (
    <div className="td-reel" role="listitem">
      {playing ? (
        <video
          ref={video}
          className="td-reel-video"
          src={reel.src}
          poster={reel.poster}
          aria-label={reel.title}
          autoPlay
          controls
          playsInline
          onEnded={onStop}
        />
      ) : (
        <button type="button" className="td-reel-btn" onClick={onPlay}>
          {/* the name is built from the visible text, so voice control can say what it sees */}
          <span className="td-sr">Play reel: </span>
          <img src={reel.poster} alt="" loading="lazy" />
          <span className="td-reel-play" aria-hidden="true">
            <PlayIcon size={28} weight="fill" />
          </span>
          <span className="td-reel-cap">
            <b>{reel.title}</b>
            <i>{clock(reel.seconds)}</i>
          </span>
        </button>
      )}
    </div>
  );
}

export default function DriveProof() {
  const [playingId, setPlayingId] = useState<string | null>(null);

  return (
    <>
      <div className="section-head section-head--center hc-fade">
        <h2>
          We have done this {" "}
          <span className="grad-text">
            @&nbsp;Mumbai <span style={{ whiteSpace: "nowrap" }}>Tech Week 2026</span>
          </span>
        </h2>
      </div>

      <div className="td-mtw hc-fade" data-reels={REELS.length}>
        <div className="td-mtw-stats">
          {STATS.map(([value, label]) => (
            <div className="td-stat" key={label}>
              <span className="td-stat-val">
                <CountUp value={value} />
              </span>
              <span className="td-stat-label">{label}</span>
            </div>
          ))}
        </div>

        <div className="td-reels" role="list" aria-label="Reels from Mumbai Tech Week">
          {REELS.map((reel) => (
            <ReelTile
              key={reel.id}
              reel={reel}
              playing={playingId === reel.id}
              onPlay={() => setPlayingId(reel.id)}
              onStop={() => setPlayingId(null)}
            />
          ))}
        </div>
      </div>
    </>
  );
}
